import { BBox, ImgProps, LayerChangeObject } from '../components/ProductWorkbenchCanvas/types';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import {
    bboxUtil,
    isArtworkEditableByName,
    isCustomArtworkImage,
    normalizePoints
} from '../components/iem_preview_section/utils';
import { batch, useDispatch, useSelector } from 'react-redux';
import { ArtworkProps, ProductChanges } from '../components/iem_preview_section/types';
import { faceplateLeftPoints } from '../components/iem_preview_section/constants';
import { Stage } from 'react-konva';
import allActions from '../actions/product_builder';
import handleLayerFlip from '../utils/flipLayer';
import handleCenterLayer from '../utils/handleCenterLayer';

const useCustomLayers = (
    onBlob: any,
    layerChanges: any,
    setLayerChanges: React.Dispatch<React.SetStateAction<ProductChanges>>,
    width: number
) => {
    const rightShell = useSelector(
        (state: any): ArtworkProps => state.rootReducer.currentShell.rightShell
    );

    const leftShell = useSelector(
        (state: any): ArtworkProps => state.rootReducer.currentShell.leftShell
    );
    const rightFaceplate = useSelector(
        (state: any): ArtworkProps => state.rootReducer.currentFaceplateOption.rightOption
    );
    const leftFaceplate = useSelector(
        (state: any): ArtworkProps => state.rootReducer.currentFaceplateOption.leftOption
    );
    const cable = useSelector((state: any) => state.rootReducer.currentCableOption.activeCable);
    const apexModule = useSelector(
        (state: any) => state.rootReducer.currentModel.currentModel?.default_options?.apexModule
    );
    const rightArtwork = useSelector(
        (state: any): ArtworkProps => state.rootReducer.currentArtwork.rightArtwork
    );
    const leftArtwork = useSelector(
        (state: any): ArtworkProps => state.rootReducer.currentArtwork.leftArtwork
    );
    // get the redux stored pos/scale/rot values and pass them to the layers below
    const customLayer = useSelector(
        (state: any): { [key: string]: LayerChangeObject } => state.rootReducer.customLayer
    );
    const isFlipped = useSelector((state: any) => state.rootReducer.customLayer.isFlipped);

    const isMirrored = useSelector((state: any) => state.rootReducer.customLayer.isMirrored);

    const currentSide = useSelector((state: any) => state.rootReducer.settingsOptions.currentSide);

    const faceplateLeftBboxBeforeOffset = useMemo<BBox>(() => bboxUtil(faceplateLeftPoints), []);
    const leftClip = useMemo(
        () => normalizePoints(faceplateLeftPoints, faceplateLeftBboxBeforeOffset),
        [faceplateLeftBboxBeforeOffset]
    );
    const faceplateLeftBbox = useMemo<BBox>(() => bboxUtil(leftClip), [leftClip]);
    const rightClip = useMemo(
        () => leftClip.map((point) => [point[0] * -1 + faceplateLeftBbox.width, point[1]]),
        [leftClip]
    );
    const faceplateRightBbox = useMemo<BBox>(() => bboxUtil(rightClip), [rightClip]);
    // const currentSide = useSelector((state: any) => state.rootReducer.settingsOptions.currentSide);
    const dispatch = useDispatch();
    const stageRef = useRef<typeof Stage>();
    const pixelRation = useMemo(() => {
        width < 800 ? 2 : 1;
    }, [width]);
    const layers: ImgProps[] = useMemo(() => {
        // check that all the layer data is NOT undefined/null before configuring them.
        if (
            ![
                rightShell,
                leftShell,
                rightFaceplate,
                leftFaceplate,
                cable,
                apexModule,
                rightArtwork,
                leftArtwork
            ].every(Boolean)
        ) {
            return [];
        }
        const res: ImgProps[] = [
            { id: 'cable', src: cable.image },
            { id: 'leftShell', src: leftShell.leftImage },
            { id: 'rightShell', src: rightShell.rightImage },
            {
                id: 'rightFaceplate',
                src: rightFaceplate.rightImage,
                blendMode: !!rightFaceplate?.blendMode,
                composition: 'multiply'
            },
            {
                id: 'leftFaceplate',
                src: leftFaceplate.leftImage,
                blendMode: !!leftFaceplate?.blendMode,
                composition: 'multiply'
            },
            { id: 'apexModule', src: apexModule.image },
            {
                // @ts-ignore
                src: leftArtwork.leftImage,
                draggable: isArtworkEditableByName(leftArtwork),
                crop: isArtworkEditableByName(leftArtwork)
                    ? null
                    : { x: 320, y: 370, width: 410, height: 440 },
                x: isCustomArtworkImage(leftArtwork) ? undefined : 320,
                y: isCustomArtworkImage(leftArtwork) ? undefined : 370,
                width: isCustomArtworkImage(leftArtwork) ? undefined : 410,
                height: isCustomArtworkImage(leftArtwork) ? undefined : 440,
                // rotation: isCustomArtworkImage(leftArtwork) ? 24.4 : undefined, // wip
                maxWidth: 410,
                maxHeight: 440,
                skewY: 0,
                skewX: 0,
                scaleX: 1,
                // offsetX: 0,
                clip: leftClip,
                clipOrigin: [295, 340],
                clipCenter: [520, 610],
                // image: imageByLayerId?.leftArtwork,
                faceplateBbox: faceplateLeftBbox,
                // containWithin: leftClip, // wip
                // smoothClip: false,
                // showClipOutline: true,
                // debugBoundaries: true,
                // showBoundingBox: true,
                ...customLayer?.leftArtwork,
                // ...layerChanges?.leftArtwork,
                id: 'leftArtwork',
                modifier: {
                    flipped: currentSide === 'both' && isFlipped,
                    mirrored: {
                        target: 'rightArtwork',
                        enabled: currentSide === 'both' && isMirrored
                    },
                    both: {
                        target: 'rightArtwork',
                        enabled: currentSide === 'both'
                    }
                }
            },
            {
                // @ts-ignore
                src: rightArtwork.rightImage,
                draggable: isArtworkEditableByName(rightArtwork),
                crop: isArtworkEditableByName(rightArtwork)
                    ? null
                    : { x: 875, y: 370, width: 410, height: 440 },
                x: isCustomArtworkImage(rightArtwork) ? undefined : 869,
                y: isCustomArtworkImage(rightArtwork) ? undefined : 364,
                width: isCustomArtworkImage(rightArtwork) ? undefined : 410,
                height: isCustomArtworkImage(rightArtwork) ? undefined : 440,
                // rotation: isCustomArtworkImage(rightArtwork) ? -24.4 : undefined, // wip
                skewY: 0,
                skewX: 0,
                scaleX: 1,
                maxWidth: 410,
                maxHeight: 440,
                // offsetX: 0,
                clip: rightClip,
                clipOrigin: [855, 340],
                clipCenter: [1078, 610],
                // image: imageByLayerId?.rightArtwork,
                faceplateBbox: faceplateRightBbox,
                // containWithin: rightClip, // wip
                // smoothClip: false,
                // debugBoundaries: true,
                ...customLayer?.rightArtwork,
                // ...layerChanges?.rightArtwork,
                id: 'rightArtwork',
                modifier: {
                    flipped: currentSide === 'both' && isFlipped,
                    mirrored: {
                        target: 'leftArtwork',
                        enabled: currentSide === 'both' && isMirrored
                    },
                    both: {
                        target: 'leftArtwork',
                        enabled: currentSide === 'both'
                    }
                }
            }
        ];
        return res.filter((layer) => Boolean(layer.src));
    }, [
        rightShell,
        leftShell,
        rightFaceplate,
        leftFaceplate,
        cable,
        apexModule,
        rightArtwork,
        leftArtwork,
        layerChanges,
        customLayer,
        currentSide
    ]);

    const onLoad = (stage: any) => {
        stageRef.current = stage;
        // @ts-ignore
        onBlob(stage.toDataURL({ quality: 1, pixelRation: pixelRation }));
    };

    useEffect(() => {
        setLayerChanges((prev = {}) => {
            let changes = {};
            if (prev['rightArtwork'] && rightArtwork.rightImage !== prev['rightArtwork'].src) {
                changes = {
                    ...changes,
                    ['rightArtwork']: {} as ImgProps
                } as ProductChanges;
            }
            if (prev['leftArtwork'] && leftArtwork.leftImage !== prev['leftArtwork'].src) {
                changes = {
                    ...changes,
                    ['leftArtwork']: {} as ImgProps
                } as ProductChanges;
            }
            return {
                ...prev,
                ...changes
            };
        });
    }, [rightArtwork, leftArtwork]);

    // Functions to control custom artwork
    const onFlip = () => {
        let draggable = layers.filter((layer) => layer.draggable);
        if (currentSide !== 'both') {
            draggable = draggable.filter((layer) => layer.id == `${currentSide}Artwork`);
        }
        const newLayers = handleLayerFlip(draggable);
        setLayerChanges(newLayers);
        // @ts-ignore
        batch(() => {
            dispatch(allActions.customLayerActions.setFlipControlButton(!isFlipped));
            dispatch(
                allActions.customLayerActions.flipCustomLayer(
                    // @ts-ignore
                    newLayers
                )
            );
        });
    };

    const onMirror = () => {
        const draggableLayer = layers.filter(
            (layer) => layer.draggable && layer.id === 'rightArtwork'
        );
        const newLayer = draggableLayer[0];
        if (newLayer) {
            newLayer.scaleX = -newLayer.scaleX;
            setLayerChanges(newLayer);
            batch(() => {
                dispatch(allActions.customLayerActions.setMirrorControlButton(!isMirrored));
                dispatch(allActions.customLayerActions.updateCustomLayerChanges(newLayer));
            });
        }
    };

    const onReset = () => {
        let leftArtworkLayer = layers.filter(
            (layer) => layer.draggable === true && layer.id === 'leftArtwork'
        );
        let rightArtworkLayer = layers.filter(
            (layer) => layer.draggable === true && layer.id === 'rightArtwork'
        );
        // Mark: resetting custom artwork layers except width and height
        if (currentSide === 'both') {
            dispatch(
                allActions.customLayerActions.resetCustomLayer({
                    // @ts-ignore
                    leftArtwork: leftArtworkLayer[0]
                        ? {
                              height: leftArtworkLayer[0].height,
                              width: leftArtworkLayer[0].width,
                              offsetX: leftArtworkLayer[0].offsetX,
                              offsetY: leftArtworkLayer[0].offsetY,
                              rotation: 0
                          }
                        : {},
                    rightArtwork: rightArtworkLayer[0]
                        ? {
                              height: rightArtworkLayer[0].height,
                              width: rightArtworkLayer[0].width,
                              offsetX: rightArtworkLayer[0].offsetX,
                              offsetY: rightArtworkLayer[0].offsetY,
                              rotation: 0
                          }
                        : {}
                })
            );
        } else if (currentSide === 'right') {
            dispatch(
                allActions.customLayerActions.resetRightCustomLayer({
                    rightArtwork: rightArtworkLayer[0]
                        ? {
                              height: rightArtworkLayer[0].height,
                              width: rightArtworkLayer[0].width,
                              offsetX: rightArtworkLayer[0].offsetX,
                              offsetY: rightArtworkLayer[0].offsetY,
                              rotation: 0
                          }
                        : {}
                })
            );
        } else {
            dispatch(
                allActions.customLayerActions.resetLeftCustomLayer({
                    leftArtwork: leftArtworkLayer[0]
                        ? {
                              height: leftArtworkLayer[0].height,
                              width: leftArtworkLayer[0].width,
                              offsetX: leftArtworkLayer[0].offsetX,
                              offsetY: leftArtworkLayer[0].offsetY,
                              rotation: 0
                          }
                        : {}
                })
            );
        }
        //@ts-ignore
        setLayerChanges({});
        setTimeout(() => {
            setLayerChanges({});

            if (stageRef.current && onBlob) {
                // @ts-ignore
                onBlob(stageRef.current.toDataURL({ quality: 1, pixelRation: pixelRation }));
            }
        });
    };

    const onCenter = () => {
        let draggable = layers.filter((layer) => layer.draggable);
        if (currentSide !== 'both') {
            draggable = draggable.filter((layer) => layer.id == `${currentSide}Artwork`);
        }
        const newLayers = handleCenterLayer(draggable);
        setLayerChanges(newLayers);
        // @ts-ignore
        dispatch(
            allActions.customLayerActions.centerCustomLayer(
                // @ts-ignore
                newLayers
            )
        );
    };

    const handleLayerChange = useCallback(
        (layerChanges: LayerChangeObject[]) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            layerChanges.map(({ image, src, ...change }) => {
                dispatch(allActions.customLayerActions.updateCustomLayerChanges(change));
            });

            if (stageRef.current && onBlob) {
                //@ts-ignore
                onBlob(stageRef.current.toDataURL({ quality: 1, pixelRation: pixelRation }));
            }
        },
        [stageRef.current, onBlob, isMirrored]
    );

    return {
        onLoad,
        onFlip,
        onMirror,
        onReset,
        onCenter,
        handleLayerChange,
        layers
    };
};
export default useCustomLayers;
